import React, { useState, useCallback } from 'react';
import { PlusCircle, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { PropertyBasicsForm } from '../components/ManualEntry/PropertyBasicsForm';
import { FinancialInputsForm } from '../components/ManualEntry/FinancialInputsForm';
import { useStandaloneAnalysis } from '../hooks/useStandaloneAnalysis';
import type { ManualDealInputs } from '../hooks/useStandaloneAnalysis';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';

interface FormState {
  address: string;
  marketId: string;
  units: number | '';
  sqft: number | '';
  yearBuilt: number | '';
  purchasePrice: number | '';
  rentPerUnit: number | '';
  capex: number | '';
  vacancyRate: number | '';
  interestRate: number | '';
  financingType: string;
}

const INITIAL_STATE: FormState = {
  address: '',
  marketId: 'philadelphia',
  units: '',
  sqft: '',
  yearBuilt: '',
  purchasePrice: '',
  rentPerUnit: '',
  capex: '',
  vacancyRate: 5,
  interestRate: '',
  financingType: 'conventional',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

export const ManualDealEntry: React.FC = () => {
  const [form, setForm] = useState<FormState>({ ...INITIAL_STATE });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const mutation = useStandaloneAnalysis();

  const handleChange = useCallback((field: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (form.units === '' || form.units < 1) {
      newErrors.units = 'Units must be at least 1';
    }
    if (form.purchasePrice === '' || form.purchasePrice <= 0) {
      newErrors.purchasePrice = 'Purchase price must be greater than 0';
    }
    if (form.rentPerUnit === '' || form.rentPerUnit < 0) {
      newErrors.rentPerUnit = 'Rent per unit must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const inputs: ManualDealInputs = {
      address: form.address.trim(),
      market_id: form.marketId,
      total_units: form.units as number,
      livable_area: form.sqft || null,
      year_built: form.yearBuilt || null,
      purchase_price: form.purchasePrice as number,
      rent_per_unit: form.rentPerUnit as number,
      capex: (form.capex as number) || 0,
      vacancy_rate: (form.vacancyRate as number) || 5,
      interest_rate: form.interestRate ? (form.interestRate as number) : null,
      financing_type: form.financingType as ManualDealInputs['financing_type'],
    };

    mutation.mutate(inputs);
  };

  const handleReset = () => {
    setForm({ ...INITIAL_STATE });
    setErrors({});
    mutation.reset();
  };

  return (
    <div className="min-h-full bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-700 px-6 py-4">
        <Breadcrumbs className="mb-3" />
        <div className="flex items-center gap-3">
          <PlusCircle className="w-6 h-6 text-gold" />
          <div>
            <h1 className="text-xl font-semibold text-ivory">New Deal Analysis</h1>
            <p className="text-sm text-stone-500">
              Enter property details for instant underwriting
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left column: Property basics */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 p-6">
              <PropertyBasicsForm
                address={form.address}
                marketId={form.marketId}
                units={form.units}
                sqft={form.sqft}
                yearBuilt={form.yearBuilt}
                errors={errors}
                onChange={handleChange}
              />
            </div>

            {/* Right column: Financial inputs */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 p-6">
              <FinancialInputsForm
                purchasePrice={form.purchasePrice}
                rentPerUnit={form.rentPerUnit}
                capex={form.capex}
                vacancyRate={form.vacancyRate}
                interestRate={form.interestRate}
                financingType={form.financingType}
                errors={errors}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center gap-4">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-gold text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <PlusCircle className="w-5 h-5" />
                  Analyze Deal
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-3 text-sm text-stone-400 hover:text-ivory transition-colors"
            >
              Reset
            </button>
          </div>
        </form>

        {/* Mutation error */}
        {mutation.isError && (
          <div className="mt-6 bg-red-500/10 border border-red-500/50 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <h3 className="font-semibold text-red-400">Analysis Failed</h3>
            </div>
            <p className="text-sm text-stone-400">{mutation.error?.message}</p>
          </div>
        )}

        {/* Results */}
        {mutation.isSuccess && mutation.data && (
          <div className="mt-6 bg-slate-900 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-ivory">Analysis Results</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {mutation.data.return_metrics && (
                <>
                  <ResultCard
                    label="Cap Rate"
                    value={mutation.data.return_metrics.cap_rate != null ? formatPercent(mutation.data.return_metrics.cap_rate) : '--'}
                    highlight={true}
                  />
                  <ResultCard
                    label="Cash-on-Cash"
                    value={mutation.data.return_metrics.cash_on_cash != null ? formatPercent(mutation.data.return_metrics.cash_on_cash) : '--'}
                  />
                  <ResultCard
                    label="5-Yr IRR"
                    value={mutation.data.return_metrics.irr != null ? formatPercent(mutation.data.return_metrics.irr) : '--'}
                  />
                  <ResultCard
                    label="Equity Multiple"
                    value={mutation.data.return_metrics.equity_multiple != null ? `${mutation.data.return_metrics.equity_multiple.toFixed(2)}x` : '--'}
                  />
                </>
              )}
              {mutation.data.noi_analysis && (
                <>
                  <ResultCard
                    label="NOI"
                    value={mutation.data.noi_analysis.noi != null ? formatCurrency(mutation.data.noi_analysis.noi) : '--'}
                  />
                  <ResultCard
                    label="DSCR"
                    value={mutation.data.noi_analysis.dscr != null ? mutation.data.noi_analysis.dscr.toFixed(2) : '--'}
                  />
                </>
              )}
              {mutation.data.financing && (
                <>
                  <ResultCard
                    label="Loan Amount"
                    value={mutation.data.financing.loan_amount != null ? formatCurrency(mutation.data.financing.loan_amount) : '--'}
                  />
                  <ResultCard
                    label="Equity Required"
                    value={mutation.data.financing.equity_required != null ? formatCurrency(mutation.data.financing.equity_required) : '--'}
                  />
                </>
              )}
            </div>

            {/* Verdict */}
            {mutation.data.deal_verdict && (
              <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-semibold ${
                    mutation.data.deal_verdict.verdict === 'STRONG BUY' ? 'text-emerald-400' :
                    mutation.data.deal_verdict.verdict === 'BUY' ? 'text-green-400' :
                    mutation.data.deal_verdict.verdict === 'HOLD' ? 'text-amber-400' :
                    'text-red-400'
                  }`}>
                    {mutation.data.deal_verdict.verdict}
                  </span>
                  {mutation.data.deal_verdict.score != null && (
                    <span className="text-xs text-stone-500">
                      Score: {mutation.data.deal_verdict.score}/100
                    </span>
                  )}
                </div>
                {mutation.data.deal_verdict.summary && (
                  <p className="text-sm text-stone-400">{mutation.data.deal_verdict.summary}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function ResultCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
      <p className="text-xs text-stone-500 mb-1">{label}</p>
      <p className={`text-lg font-mono font-semibold ${highlight ? 'text-gold' : 'text-ivory'}`}>
        {value}
      </p>
    </div>
  );
}

export default ManualDealEntry;
